/**
 * RUNE Store
 *
 * Zustand store for managing RUNE conversational AI state.
 * Handles SSE streaming, conversation lifecycle, and page context.
 *
 * SSE Format: data-only (no event: lines). Event type is inside the JSON `type` field.
 * Supported types: text_delta, tool_call_start, tool_call_complete, done, error
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { apiFetch } from '@/lib/apiFetch';
import {
  isOrgApiKeyRequiredError,
  handleOrgApiKeyRequired,
} from '@/lib/session-handler';
import type { StructuredResponse } from '@/types/runeStructured';

// Rune chat router. rune-service mounts rune_ogi.router directly at /v1/rune
// (no inner /rune prefix), so paths are /v1/rune/start, /v1/rune/message, etc.

// ========== TYPES ==========

export interface ToolCallRecord {
  tool: string;
  args: Record<string, unknown>;
  startedAt: number;        // Date.now() when started
  durationMs?: number;      // From backend tool_call_complete
  resultSummary?: string;   // From backend tool_call_complete
}

export interface RUNEMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: ToolCallRecord[];
  timestamp: string;
}

export interface RUNEPageContext {
  page:
    | 'overview'
    | 'projects'
    | 'pipeline'
    | 'playbooks'
    | 'sales_prep'
    | 'analytics'
    | 'roadmap'
    | 'council'
    | 'org_overview'
    | 'department_detail'
    | 'job_role_analysis'
    | 'rune';
  organizationId: string;
  runId?: string;
  departmentName?: string;
  roleName?: string;
  selectedProjectId?: string;
  selectedPlaybookLevel?: string;
}

// ========== STORE STATE SHAPE ==========

interface RUNEState {
  // Conversation
  conversationId: string | null;
  messages: RUNEMessage[];
  isStreaming: boolean;
  streamingContent: string;
  activeToolCall: { tool: string; description: string } | null;
  pendingToolCalls: ToolCallRecord[];

  // Context
  organizationId: string | null;
  runId: string | null;
  pageContext: RUNEPageContext | null;
  runSummary: {
    companyName: string;
    opportunityCount: number;
    validatedCount: number;
  } | null;

  // Canvas
  canvasMode: boolean;
  canvasContent: { type: string; data: unknown } | null;

  // Structured responses (slice 2 of /discover plan).
  // Only populated when the new page calls askIntent(); old surfaces never set this.
  structuredResponse: StructuredResponse | null;

  // History pills (A6) — one entry per unique intent asked in this session.
  // Populated when a structured_response SSE event arrives AND rehydrated
  // from persisted message payloads on startConversation/loadHistory.
  structuredHistory: StructuredHistoryEntry[];

  // User-visible error surface (E2 partial). Set when an SSE stream errors
  // or a network request fails mid-ask. DiscoverPage reads this and shows
  // a dismissible banner. Cleared on next successful ask or by clearError().
  lastError: string | null;

  // Actions
  startConversation: (
    orgId: string,
    pageContext: RUNEPageContext,
    idToken: string,
    runId?: string
  ) => Promise<void>;
  sendMessage: (content: string, idToken: string) => Promise<void>;
  askIntent: (
    intentId: string,
    questionText: string,
    idToken: string,
    params?: Record<string, unknown>
  ) => Promise<void>;
  clearStructuredResponse: () => void;
  restoreStructuredFromHistory: (intentId: string) => void;
  clearError: () => void;
  loadHistory: (conversationId: string, idToken: string) => Promise<void>;
  setPageContext: (ctx: RUNEPageContext) => void;
  setCanvasMode: (active: boolean) => void;
  setCanvasContent: (content: { type: string; data: unknown } | null) => void;
  reset: () => void;
}

// ========== HELPERS ==========

export interface StructuredHistoryEntry {
  intentId: string;
  label: string;
  payload: StructuredResponse;
}

// Cap the pill row at this many unique intents. Long sessions on the same
// conversation otherwise accumulate payloads forever, each one a full
// structured response that can be tens of KB. 10 keeps the pill row compact
// and visually scannable; realistic demo sessions rarely exceed this anyway.
export const STRUCTURED_HISTORY_LIMIT = 10;

/**
 * Scan raw history messages (from /start resumed or /history) for the
 * most-recent assistant message that carries a structured_payload. Used
 * to restore the structured response view on refresh for /discover.
 * Returns null when no such message exists (new conversation, or only
 * text-Rune messages).
 */
function findLatestStructuredPayload(
  rawMessages: Array<{ role?: string; structured_payload?: unknown }>,
): StructuredResponse | null {
  for (let i = rawMessages.length - 1; i >= 0; i--) {
    const msg = rawMessages[i];
    if (msg.role === 'assistant' && msg.structured_payload) {
      return msg.structured_payload as StructuredResponse;
    }
  }
  return null;
}

/**
 * Build the structured history (one entry per unique intent) from resumed
 * messages, preserving chronological order and keeping the LATEST payload
 * when the same intent_id appears multiple times. Used on /start resumed
 * and /history to rehydrate the pill row.
 */
function collectStructuredHistory(
  rawMessages: Array<{ role?: string; structured_payload?: unknown }>,
): StructuredHistoryEntry[] {
  // Map: intentId → entry (last one wins). Use insertion order by tracking
  // keys separately so reordering to "most recent last" is straightforward.
  const byIntent = new Map<string, StructuredHistoryEntry>();
  for (const msg of rawMessages) {
    if (msg.role !== 'assistant' || !msg.structured_payload) continue;
    const payload = msg.structured_payload as StructuredResponse;
    const intentId = payload.intent_id;
    if (!intentId) continue;
    byIntent.delete(intentId); // remove prior so re-inserting moves to end
    byIntent.set(intentId, {
      intentId,
      label: payload.title,
      payload,
    });
  }
  const all = Array.from(byIntent.values());
  // Keep the most-recent N. We want the tail because dedup keeps the newer
  // payload under the same intent key; slicing from the end preserves recency.
  return all.slice(-STRUCTURED_HISTORY_LIMIT);
}

function mergeHistoryEntry(
  history: StructuredHistoryEntry[],
  entry: StructuredHistoryEntry,
): StructuredHistoryEntry[] {
  // Same dedup-and-append rule as collectStructuredHistory: one entry per
  // intent, most recent is last so the pill row reads left→right = oldest→newest.
  // Cap at STRUCTURED_HISTORY_LIMIT by dropping the oldest entries.
  const filtered = history.filter((h) => h.intentId !== entry.intentId);
  const next = [...filtered, entry];
  return next.length > STRUCTURED_HISTORY_LIMIT
    ? next.slice(next.length - STRUCTURED_HISTORY_LIMIT)
    : next;
}

// ========== INITIAL STATE ==========

const initialState: Pick<
  RUNEState,
  | 'conversationId'
  | 'messages'
  | 'isStreaming'
  | 'streamingContent'
  | 'activeToolCall'
  | 'pendingToolCalls'
  | 'organizationId'
  | 'runId'
  | 'pageContext'
  | 'runSummary'
  | 'canvasMode'
  | 'canvasContent'
  | 'structuredResponse'
  | 'structuredHistory'
  | 'lastError'
> = {
  conversationId: null,
  messages: [],
  isStreaming: false,
  streamingContent: '',
  activeToolCall: null,
  pendingToolCalls: [],
  organizationId: null,
  runId: null,
  pageContext: null,
  runSummary: null,
  canvasMode: false,
  canvasContent: null,
  structuredResponse: null,
  structuredHistory: [],
  lastError: null,
};

// ========== STORE IMPLEMENTATION ==========

// Module-level AbortController for the in-flight Rune SSE fetch.
// Kept outside zustand state because AbortController isn't serializable
// and doesn't need to participate in devtools replay. reset() aborts it
// so an in-flight fetch from Org A can't post its final chunks into
// Org B's state after a soft switch.
let runeInflightController: AbortController | null = null;

function abortInflightRuneFetch(): void {
  if (runeInflightController) {
    runeInflightController.abort();
    runeInflightController = null;
  }
}

export const useRuneStore = create<RUNEState>()(
  devtools(
    (set, get) => ({
      ...initialState,

        // ========== START CONVERSATION ==========

        startConversation: async (orgId, pageContext, idToken, runId?) => {
          // Guard against double-start (auto-start from floating button + sidepanel race)
          if (get().isStreaming || get().conversationId) return;
          set({ isStreaming: true });
          try {
            const response = await apiFetch(`/v1/rune/start`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
                'X-Organization-Id': orgId,
              },
              body: JSON.stringify({
                organization_id: orgId,
                run_id: runId || null,
                page_context: pageContext,
              }),
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
              if (isOrgApiKeyRequiredError(response.status, errorData.error)) {
                handleOrgApiKeyRequired();
              }
              throw new Error(errorData.message || errorData.error || `Failed to start conversation: ${response.status}`);
            }

            const data = await response.json();

            if (data.resumed && data.messages) {
              // Resumed existing conversation — load messages directly
              const mappedMessages: RUNEMessage[] = data.messages.map(
                (msg: { id: string; role: string; content: string; created_at?: string }) => ({
                  id: msg.id,
                  role: msg.role as 'user' | 'assistant',
                  content: msg.content,
                  timestamp: msg.created_at || new Date().toISOString(),
                })
              );
              // Restore the most-recent structured response (if any) so
              // /discover survives a refresh. Old surfaces don't read
              // structuredResponse, so they're unaffected. Also rebuild the
              // history-pill row from all persisted structured payloads.
              const restoredPayload = findLatestStructuredPayload(data.messages);
              const restoredHistory = collectStructuredHistory(data.messages);
              set({
                conversationId: data.conversation_id,
                organizationId: data.organization_id || orgId,
                messages: mappedMessages,
                runId: data.run_id || runId || null,
                pageContext,
                isStreaming: false,
                structuredResponse: restoredPayload,
                structuredHistory: restoredHistory,
              });
            } else {
              // New conversation — greeting as first message
              set({
                conversationId: data.conversation_id,
                organizationId: data.organization_id || orgId,
                messages: [
                  {
                    id: `assistant-${Date.now()}`,
                    role: 'assistant',
                    content: data.greeting || data.content || '',
                    timestamp: new Date().toISOString(),
                  },
                ],
                runId: runId || null,
                runSummary: data.run_summary || null,
                pageContext,
                isStreaming: false,
              });
            }
          } catch (error) {
            console.error('Failed to start RUNE conversation:', error);
            set({ isStreaming: false });
          }
        },

        // ========== SEND MESSAGE (SSE STREAMING) ==========

        sendMessage: async (content, idToken) => {
          const { conversationId, pageContext, messages } = get();
          if (!conversationId) {
            console.error('No active conversation');
            return;
          }

          // Add user message optimistically
          const userMsg: RUNEMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content,
            timestamp: new Date().toISOString(),
          };

          set({
            messages: [...messages, userMsg],
            isStreaming: true,
            streamingContent: '',
            activeToolCall: null,
            pendingToolCalls: [],
            lastError: null,
          });

          try {
            const orgId = get().organizationId;
            abortInflightRuneFetch();
            const controller = new AbortController();
            runeInflightController = controller;
            const response = await apiFetch(`/v1/rune/message`, {
              method: 'POST',
              signal: controller.signal,
              headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
                ...(orgId ? { 'X-Organization-Id': orgId } : {}),
              },
              body: JSON.stringify({
                conversation_id: conversationId,
                content,
                page_context: pageContext,
              }),
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
              if (isOrgApiKeyRequiredError(response.status, errorData.error)) {
                handleOrgApiKeyRequired();
              }
              throw new Error(errorData.message || errorData.error || `RUNE message failed: ${response.status}`);
            }

            const reader = response.body!.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                if (!line.startsWith('data: ')) continue;
                try {
                  const data = JSON.parse(line.slice(6));
                  switch (data.type) {
                    case 'text_delta':
                      set((state) => ({
                        streamingContent: state.streamingContent + data.content,
                      }));
                      break;
                    case 'tool_call_start':
                      set((state) => ({
                        activeToolCall: {
                          tool: data.tool,
                          description: `Looking up ${data.tool}...`,
                        },
                        pendingToolCalls: [
                          ...state.pendingToolCalls,
                          {
                            tool: data.tool,
                            args: data.args || {},
                            startedAt: Date.now(),
                          },
                        ],
                      }));
                      break;
                    case 'tool_call_complete':
                      set((state) => ({
                        activeToolCall: null,
                        pendingToolCalls: state.pendingToolCalls.map((tc, i) =>
                          i === state.pendingToolCalls.length - 1
                            ? { ...tc, durationMs: data.duration_ms, resultSummary: data.result_summary }
                            : tc
                        ),
                      }));
                      break;
                    case 'visualization':
                      set({
                        canvasContent: {
                          type: data.viz_type,
                          data: data.data,
                        },
                        canvasMode: true,
                      });
                      break;
                    case 'structured_response':
                      // Slice 2 of /discover plan. Old surfaces never
                      // trigger this event (no intent_id in their requests),
                      // so behavior is preserved for them.
                      set({
                        structuredResponse: data.data as StructuredResponse,
                      });
                      break;
                    case 'done':
                      set((state) => ({
                        messages: [
                          ...state.messages,
                          {
                            id: `assistant-${Date.now()}`,
                            role: 'assistant' as const,
                            content: data.content || state.streamingContent,
                            toolCalls: state.pendingToolCalls.length > 0
                              ? state.pendingToolCalls
                              : undefined,
                            timestamp: new Date().toISOString(),
                          },
                        ],
                        streamingContent: '',
                        isStreaming: false,
                        activeToolCall: null,
                        pendingToolCalls: [],
                      }));
                      break;
                    case 'error':
                      console.error('RUNE SSE error:', data.content);
                      set({
                        isStreaming: false,
                        streamingContent: '',
                        activeToolCall: null,
                        pendingToolCalls: [],
                        lastError:
                          typeof data.content === 'string' && data.content
                            ? data.content
                            : 'Something went wrong while Rune was answering.',
                      });
                      break;
                  }
                } catch (parseErr) {
                  console.warn('Failed to parse SSE event:', line, parseErr);
                }
              }
            }
          } catch (error) {
            console.error('RUNE sendMessage error:', error);
            set({
              isStreaming: false,
              streamingContent: '',
              activeToolCall: null,
              pendingToolCalls: [],
              lastError:
                error instanceof Error
                  ? error.message
                  : 'Network error while sending your question.',
            });
          }
        },

        // ========== ASK INTENT (STRUCTURED RESPONSE) ==========
        // Slice 2 of /discover plan. Sends an `intent_id` to the backend
        // which gates emission of `structured_response` SSE events. Mirrors
        // sendMessage's flow but is intentionally a separate code path so
        // sendMessage stays untouched (old surfaces never call this).

        askIntent: async (intentId, questionText, idToken, params) => {
          const { conversationId, pageContext, messages } = get();
          if (!conversationId) {
            console.error('No active conversation');
            return;
          }

          const userMsg: RUNEMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: questionText,
            timestamp: new Date().toISOString(),
          };

          set({
            messages: [...messages, userMsg],
            isStreaming: true,
            streamingContent: '',
            activeToolCall: null,
            pendingToolCalls: [],
            structuredResponse: null,
            lastError: null,
          });

          try {
            const orgId = get().organizationId;
            // Abort any prior in-flight fetch before starting a new one,
            // then hold the controller so reset() can cancel this one.
            abortInflightRuneFetch();
            const controller = new AbortController();
            runeInflightController = controller;
            const response = await apiFetch(`/v1/rune/message`, {
              method: 'POST',
              signal: controller.signal,
              headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
                ...(orgId ? { 'X-Organization-Id': orgId } : {}),
              },
              body: JSON.stringify({
                conversation_id: conversationId,
                content: questionText,
                page_context: pageContext,
                intent_id: intentId,
                ...(params && Object.keys(params).length > 0
                  ? { intent_params: params }
                  : {}),
              }),
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
              if (isOrgApiKeyRequiredError(response.status, errorData.error)) {
                handleOrgApiKeyRequired();
              }
              throw new Error(errorData.message || errorData.error || `RUNE intent failed: ${response.status}`);
            }

            const reader = response.body!.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                if (!line.startsWith('data: ')) continue;
                try {
                  const data = JSON.parse(line.slice(6));
                  switch (data.type) {
                    case 'structured_response': {
                      const payload = data.data as StructuredResponse;
                      set((state) => ({
                        structuredResponse: payload,
                        structuredHistory: mergeHistoryEntry(state.structuredHistory, {
                          intentId: payload.intent_id,
                          label: payload.title,
                          payload,
                        }),
                      }));
                      break;
                    }
                    case 'done':
                      set((state) => ({
                        messages: [
                          ...state.messages,
                          {
                            id: `assistant-${Date.now()}`,
                            role: 'assistant' as const,
                            content: data.content || state.structuredResponse?.title || '',
                            timestamp: new Date().toISOString(),
                          },
                        ],
                        streamingContent: '',
                        isStreaming: false,
                        activeToolCall: null,
                        pendingToolCalls: [],
                      }));
                      break;
                    case 'error':
                      console.error('RUNE intent SSE error:', data.content);
                      set({
                        isStreaming: false,
                        streamingContent: '',
                        activeToolCall: null,
                        pendingToolCalls: [],
                        lastError:
                          typeof data.content === 'string' && data.content
                            ? data.content
                            : 'Something went wrong while Rune was answering.',
                      });
                      break;
                    // Ignore text_delta / tool_call_* — structured intents
                    // don't emit them today, but if a future intent does
                    // (e.g. narrative prose alongside structured data),
                    // we'd handle them here.
                  }
                } catch (parseErr) {
                  console.warn('Failed to parse SSE event:', line, parseErr);
                }
              }
            }
          } catch (error) {
            console.error('RUNE askIntent error:', error);
            set({
              isStreaming: false,
              streamingContent: '',
              activeToolCall: null,
              pendingToolCalls: [],
              lastError:
                error instanceof Error
                  ? error.message
                  : 'Network error while sending your question.',
            });
          }
        },

        clearStructuredResponse: () => {
          set({ structuredResponse: null });
        },

        clearError: () => {
          set({ lastError: null });
        },

        // ========== HISTORY PILL RESTORE ==========
        // Cheap restore from in-memory history — no network call. The pill row
        // is derived from structuredHistory, so clicking one instantly replays
        // a cached payload. Data can be stale (built at the time of original
        // ask); users can re-click the theme card to re-dispatch for fresh data.
        restoreStructuredFromHistory: (intentId) => {
          const entry = get().structuredHistory.find(
            (h) => h.intentId === intentId,
          );
          if (!entry) return;
          set({ structuredResponse: entry.payload });
        },

        // ========== LOAD HISTORY ==========

        loadHistory: async (conversationId, idToken) => {
          try {
            const response = await apiFetch(`/v1/rune/history/${conversationId}`, {
              headers: {
                Authorization: `Bearer ${idToken}`,
              },
            });

            if (!response.ok) {
              throw new Error(`Failed to load history: ${response.status}`);
            }

            const data = await response.json();

            const mappedMessages: RUNEMessage[] = (data.messages || []).map(
              (msg: { role: string; content: string; created_at?: string }, i: number) => ({
                id: `${msg.role}-history-${i}`,
                role: msg.role as 'user' | 'assistant',
                content: msg.content,
                timestamp: msg.created_at || new Date().toISOString(),
              })
            );

            const restoredPayload = findLatestStructuredPayload(data.messages || []);
            const restoredHistory = collectStructuredHistory(data.messages || []);

            set({
              messages: mappedMessages,
              conversationId,
              organizationId: data.organization_id || get().organizationId,
              runId: data.run_id || get().runId,
              structuredResponse: restoredPayload,
              structuredHistory: restoredHistory,
            });
          } catch (error) {
            console.error('Failed to load RUNE history:', error);
          }
        },

        // ========== CONTEXT ==========

        setPageContext: (ctx) => {
          set({ pageContext: ctx });
        },

        // ========== CANVAS ==========

        setCanvasMode: (active) => {
          set({ canvasMode: active });
        },

        setCanvasContent: (content) => {
          set({ canvasContent: content, canvasMode: content !== null });
        },

        // ========== RESET ==========

        reset: () => {
          // Abort any in-flight Rune fetch so its remaining SSE chunks
          // don't land in the fresh (post-switch) state. AbortError is
          // caught by the fetch's try/catch and surfaced as lastError
          // — but reset() will be called again below which wipes that.
          abortInflightRuneFetch();
          set(initialState);
        },
      }),
    { name: 'RUNE' }
  )
);
